import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Settings,
  Menu,
  X,
  LogOut,
  Activity,
  Crown,
  FolderOpen,
  CheckCircle,
  Edit3,
  GitBranch,
  BarChart3,
  UserPlus,
  Hash,
  Wrench
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../Logo';
import RoleSwitcher from '../RoleSwitcher/RoleSwitcher';

// Navigation items for Super Admin
const superAdminNavigationItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    title: 'System Health',
    icon: Activity,
    path: '/health',
  },
  {
    title: 'Tenants',
    icon: Building2,
    path: '/tenants',
  },
  {
    title: 'Tenant Admins',
    icon: Users,
    path: '/admins',
  },
  {
    title: 'Content Team',
    icon: FileText,
    path: '/analysts',
  },
  {
    title: 'Super Admins',
    icon: Crown,
    path: '/super-admins',
  },
  {
    title: 'Questions',
    icon: FileText,
    path: '/questions',
  },
  {
    title: 'UUID Helper',
    icon: Wrench,
    path: '/uuid-helper',
  },
  {
    title: 'Settings',
    icon: Settings,
    path: '/settings',
  },
];

// Navigation items for Content Supervisor
const contentSupervisorNavigationItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    title: 'KIT Management',
    icon: FolderOpen,
    path: '/kits',
  },
  {
    title: 'Draft Questions',
    icon: FileText,
    path: '/draft-questions',
  },
  {
    title: 'Workflow',
    icon: GitBranch,
    path: '/workflow',
  },
  {
    title: 'Team Management',
    icon: Users,
    path: '/team',
  },
  {
    title: 'Analytics',
    icon: BarChart3,
    path: '/analytics',
  },
];

// Navigation items for Content Generator
const contentGeneratorNavigationItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    title: 'Create Questions',
    icon: Edit3,
    path: '/questions/create',
  },
  {
    title: 'My Questions',
    icon: FileText,
    path: '/my-questions',
  },
  {
    title: 'Tags',
    icon: Hash,
    path: '/tags',
  },
  {
    title: 'Bulk Upload',
    icon: FileText,
    path: '/questions/bulk-upload',
  },
];

// Navigation items for Content Reviewer
const contentReviewerNavigationItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
  },
  {
    title: 'Review Queue',
    icon: CheckCircle,
    path: '/questions-for-review',
  },
  {
    title: 'My Assignments',
    icon: FileText,
    path: '/assigned-questions',
  },
  {
    title: 'Completed Reviews',
    icon: CheckCircle,
    path: '/completed-reviews',
  },
];



const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, activeRole } = useAuth();

  // Get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'CONTENT_SUPERVISOR':
        return 'Content Supervisor';
      case 'CONTENT_GENERATOR':
        return 'Content Generator';
      case 'CONTENT_REVIEWER':
        return 'Content Reviewer';
      case 'SUPER_ADMIN':
        return 'Super Admin';
      default:
        return 'User';
    }
  };

  // Get navigation items based on active role (for Super Admin) or user role
  const getNavigationItems = () => {
    const roleToCheck = user?.isSuperAdmin ? activeRole : user?.role;

    switch (roleToCheck) {
      case 'CONTENT_SUPERVISOR':
        return contentSupervisorNavigationItems;
      case 'CONTENT_GENERATOR':
        return contentGeneratorNavigationItems;
      case 'CONTENT_REVIEWER':
        return contentReviewerNavigationItems;
      case 'SUPER_ADMIN':
      default:
        return superAdminNavigationItems;
    }
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Close sidebar when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint in Tailwind
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle body class to prevent scrolling when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('sidebar-open');
    };
  }, [sidebarOpen]);

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col">
        <div className="flex flex-col flex-grow overflow-y-auto bg-white border-r border-slate-200 shadow-sm">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0 px-6 py-6">
            <Logo size="md" showText={true} />
          </div>

          {/* Navigation */}
          <div className="flex-grow flex flex-col">
            <nav className="flex-1 px-4 space-y-1">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActiveRoute(item.path);

                return (
                  <button
                    key={item.title}
                    onClick={() => navigate(item.path)}
                    className={`${
                      isActive
                        ? 'bg-blue-50 text-blue-900 border-r-2 border-blue-500'
                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                    } group flex items-center px-3 py-2 text-sm font-medium w-full text-left transition-all duration-200`}
                  >
                    <Icon
                      className={`${
                        isActive ? 'text-blue-600' : 'text-slate-500 group-hover:text-slate-700'
                      } mr-3 h-4 w-4 transition-colors duration-200`}
                    />
                    {item.title}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* User info */}
          <div className="flex-shrink-0 border-t border-slate-200 p-4">
            <div className="flex flex-col space-y-3">
              <div className="flex items-center min-w-0">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                    <Users className="h-4 w-4 text-slate-600" />
                  </div>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Super Admin'}</p>
                  <p className="text-xs text-slate-600 truncate">{user?.email || 'superadmin@bayesprep.com'}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {user?.isSuperAdmin ? getRoleDisplayName(activeRole) : getRoleDisplayName(user?.role)}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-slate-600 flex items-center justify-center space-x-1 px-2 py-1 rounded transition-colors duration-200 w-full"
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
                <span className="text-xs">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>

            {/* Mobile sidebar content */}
            <div className="flex flex-col flex-grow overflow-y-auto">
              {/* Logo */}
              <div className="flex items-center flex-shrink-0 px-6 py-6">
                <Logo size="md" showText={true} />
              </div>

              {/* Navigation */}
              <div className="flex-grow flex flex-col">
                <nav className="flex-1 px-4 space-y-1">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActiveRoute(item.path);

                    return (
                      <button
                        key={item.title}
                        onClick={() => {
                          navigate(item.path);
                          setSidebarOpen(false);
                        }}
                        className={`${
                          isActive
                            ? 'bg-blue-50 text-blue-900 border-r-2 border-blue-500'
                            : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                        } group flex items-center px-3 py-2 text-sm font-medium w-full text-left transition-all duration-200`}
                      >
                        <Icon
                          className={`${
                            isActive ? 'text-blue-600' : 'text-slate-500 group-hover:text-slate-700'
                          } mr-3 h-4 w-4 transition-colors duration-200`}
                        />
                        {item.title}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* User info */}
              <div className="flex-shrink-0 border-t border-slate-200 p-4">
                <div className="flex flex-col space-y-3">
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center">
                        <Users className="h-4 w-4 text-slate-600" />
                      </div>
                    </div>
                    <div className="ml-3 min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{user?.name || 'Super Admin'}</p>
                      <p className="text-xs text-slate-600 truncate">{user?.email || 'superadmin@bayesprep.com'}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {user?.isSuperAdmin ? getRoleDisplayName(activeRole) : getRoleDisplayName(user?.role)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-slate-400 hover:text-slate-600 flex items-center justify-center space-x-1 px-2 py-1 rounded transition-colors duration-200 w-full"
                    title="Logout"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="text-xs">Logout</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Desktop header - only visible on desktop */}
        <div className="hidden md:flex relative z-10 flex-shrink-0 h-16 bg-white border-b border-slate-200 shadow-sm">
          <div className="flex-1 px-6 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-900">
              {user?.isSuperAdmin ? getRoleDisplayName(activeRole) : getRoleDisplayName(user?.role)} Portal
            </h1>
            {/* Role Switcher for Super Admin */}
            {user?.isSuperAdmin && (
              <div className="flex items-center space-x-4">
                <RoleSwitcher />
              </div>
            )}
          </div>
        </div>

        {/* Mobile menu button - only visible on mobile */}
        <div className="md:hidden relative z-10 flex-shrink-0 flex h-16 bg-white border-b border-slate-200 shadow-sm">
          <button
            className="px-4 border-r border-slate-200 text-slate-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1 px-6 flex items-center justify-between">
            <h1 className="text-lg font-semibold text-slate-900">
              {user?.isSuperAdmin ? getRoleDisplayName(activeRole) : getRoleDisplayName(user?.role)} Portal
            </h1>
            {/* Role Switcher for Super Admin on mobile */}
            {user?.isSuperAdmin && (
              <div className="flex items-center">
                <RoleSwitcher />
              </div>
            )}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none bg-slate-50">
          <div className="py-4 md:py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-12">
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
